import type {Mapper} from '@automapper/core';
import {type DownloadColumn, type IDownloadByLinkService, ServerResponseStatus} from '@igp/shared/api';
import type {AgentRefereeRevenueReport} from '@igp/shared/contract/pam';
import {map} from '@igp/shared/otel';
import {
    type DownloadStrategyRequest,
    type DownloadStrategyResponse,
    EntityDownloadClientStrategy,
    type IDownloadStrategy,
} from '@igp/shared/redux/download';
import type {IEntityReadService} from '@igp/shared/redux/entity';
import {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {agentLevelReportV2ServiceTypes} from './constants';
import {agentLevelReportV2MappingKeys} from './mapper';
import type {AgentLevelReportV2DownloadFields, AgentLevelReportV2ViewModelsKeys} from './types';

@injectable()
export class AgentLevelReportV2DownloadClientStrategy extends EntityDownloadClientStrategy<AgentLevelReportV2ViewModelsKeys, EntityType> {
    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(agentLevelReportV2ServiceTypes.service) service: IEntityReadService<EntityType, AgentRefereeRevenueReport>
    ) {
        super(mapper, service, EntityType.AgentLevelReporV2, {
            viewModelKeys: agentLevelReportV2MappingKeys.ViewModelsKeys,
            queryFields: agentLevelReportV2MappingKeys.QueryKeys,
            model: agentLevelReportV2MappingKeys.Model,
            downloadModel: agentLevelReportV2MappingKeys.DownloadModel,
        });
    }
}

@injectable()
export class AgentLevelReportV2DownloadServerStrategy implements IDownloadStrategy<AgentLevelReportV2ViewModelsKeys> {
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<AgentLevelReportV2DownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(agentLevelReportV2ServiceTypes.service) service: IDownloadByLinkService<AgentLevelReportV2DownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(
        request: DownloadStrategyRequest<AgentLevelReportV2ViewModelsKeys>
    ): Observable<DownloadStrategyResponse<AgentLevelReportV2ViewModelsKeys>> {
        const columns: DownloadColumn<AgentLevelReportV2DownloadFields>[] = request.keys?.map<
            DownloadColumn<AgentLevelReportV2DownloadFields>
        >(k => {
            const queryField = this._mapper.map<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2DownloadFields>(
                k,
                agentLevelReportV2MappingKeys.ViewModelsKeys,
                agentLevelReportV2MappingKeys.DownloadFields
            );
            return {
                name: request.headers[k] ?? k,
                field: queryField,
            };
        });
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}
